import {
  EuiCallOut,
  EuiEmptyPrompt,
  EuiIcon,
  EuiLink,
  EuiLoadingLogo,
  EuiMark,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from '@elastic/eui';
import type { MouseEvent, ReactNode } from 'react';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { getDomicileURL } from '../../model';
import { PageContext } from '../../page_container';

export enum SplashScreenMode {
  Default = 'Default',
  NoData = 'NoData',
  Loading = 'Loading',
}

export interface Params {
  mode: SplashScreenMode;
}

export function AssetsSplashScreen({ mode }: Params) {
  const { domicileCode } = useContext(PageContext);
  const navigate = useNavigate();

  const [icon, title, body]: [ReactNode, string, string] = useMemo(() => {
    switch (mode) {
      case 'NoData':
        return [
          <EuiIcon key="search-no-data" type={'search'} size={'xl'} />,
          'There are no assets to display',
          'Try adjusting your search query',
        ];
      case 'Loading':
        return [
          <EuiLoadingLogo key="search-loading" logo="search" size="xl" />,
          'Searching assets…',
          'Use search bar to narrow down the search',
        ];
      default:
        return [
          <EuiIcon key="search-empty" type={'search'} size={'xl'} />,
          'Explore assets and learn what ETFs hold them',
          'Use search bar to narrow down the search',
        ];
    }
  }, [mode]);

  return (
    <EuiEmptyPrompt
      icon={icon}
      title={<h2>{title}</h2>}
      titleSize="s"
      style={{ maxWidth: '60em', display: 'flex' }}
      body={
        <div>
          <p>{body}</p>
          <EuiPanel color={'primary'} title={'Search query hints'}>
            <EuiCallOut title={'Search query hints'} iconType={'help'} color={'primary'} style={{ padding: 0 }}>
              <EuiPanel color={'warning'}>
                Go to the{' '}
                <EuiLink
                  href={getDomicileURL(domicileCode, '/')}
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    navigate(getDomicileURL(domicileCode, '/'));
                  }}
                >
                  Funds page
                </EuiLink>{' '}
                if you want to search for ETFs
              </EuiPanel>
              <EuiSpacer size="s" />
              <div style={{ textAlign: 'left' }}>
                <EuiText size="s">
                  <EuiMark>US02079K3059</EuiMark> - displays asset with ISIN <strong>US02079K3059</strong>
                </EuiText>
                <EuiSpacer size="m" />
                <EuiText size="s">
                  <EuiMark>Alphabet OR &quot;Meta Platforms&quot;</EuiMark> or{' '}
                  <EuiMark>Alphabet &quot;Meta Platforms&quot;</EuiMark> - displays all assets with{' '}
                  <strong>Alphabet</strong> or <strong>Meta Platforms</strong> in the name (<strong>OR</strong> should
                  be capitalized)
                </EuiText>
                <EuiSpacer size="m" />
                <EuiText size="s">
                  <EuiMark>Hyundai AND Industries</EuiMark> - displays all assets with both <strong>Hyundai</strong> and{' '}
                  <strong>Industries</strong> in the name (<strong>AND</strong> should be capitalized)
                </EuiText>
                <EuiSpacer size="m" />
                <EuiText size="s">
                  <EuiMark>Hyundai -Industries</EuiMark> - displays all assets with <strong>Hyundai</strong> in the name
                  excluding those with <strong>Industries</strong>
                </EuiText>
              </div>
            </EuiCallOut>
          </EuiPanel>
        </div>
      }
    />
  );
}
